import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase'; 
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function BehaviorTrendChart({ tenantId, studentId }) {
  const [chartData, setChartData] = useState([]);
  const [metricKeys, setMetricKeys] = useState([]);
  const [scaleConfig, setScaleConfig] = useState({ domain: [1, 5], ticks: [1,2,3,4,5], dictionary: {} });
  const [isLoading, setIsLoading] = useState(true);

  const colors = ["#003366", "#28a745", "#ba0c2f", "#f0ad4e", "#6f42c1", "#17a2b8"];

  useEffect(() => {
    const fetchAndAggregateData = async () => {
      try {
        if (!studentId || !tenantId) return;

        // 1. Fetch Student to get their building ID
        const studentRef = doc(db, `districts/${tenantId}/students/${studentId}`);
        const studentSnap = await getDoc(studentRef);
        const schoolId = studentSnap.exists() ? (studentSnap.data().schoolId || 'District Office') : 'District Office';

        // 2. Build the Dynamic Recharts Domain
        const configRef = doc(db, `districts/${tenantId}/settings`, `rubric_${schoolId}`);
        const configSnap = await getDoc(configRef);
        
        let customScale = [
          { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
        ];

        if (configSnap.exists() && configSnap.data().customScale) {
          customScale = configSnap.data().customScale;
        }

        const minVal = Math.min(...customScale.map(btn => btn.value));
        const maxVal = Math.max(...customScale.map(btn => btn.value));
        const customTicks = customScale.map(btn => btn.value);
        
        // Creates a dictionary like: { 1: '☹️', 2: '😐', 3: '🙂' } so the chart can translate math into UI
        const labelDictionary = customScale.reduce((acc, curr) => {
          acc[curr.value] = curr.label;
          return acc;
        }, {});

        setScaleConfig({
          domain: [minVal, maxVal],
          ticks: customTicks,
          dictionary: labelDictionary
        });

        // 3. Fetch Data Logs
        const logsRef = collection(db, `districts/${tenantId}/students/${studentId}/behaviorLogs`);
        const logsQuery = query(logsRef, orderBy("timestamp", "asc"));
        const snapshot = await getDocs(logsQuery);

        // 4. Dynamic Aggregation Engine
        const groupedBySprint = {};
        const uniqueMetrics = new Set();

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const sprintName = data.sprint.split(':')[0]; 

          if (!groupedBySprint[sprintName]) {
             groupedBySprint[sprintName] = { count: 0, scores: {} };
          }
          
          groupedBySprint[sprintName].count += 1;

          if (data.scores) {
            Object.entries(data.scores).forEach(([metric, val]) => {
              uniqueMetrics.add(metric);
              if (!groupedBySprint[sprintName].scores[metric]) groupedBySprint[sprintName].scores[metric] = 0;
              groupedBySprint[sprintName].scores[metric] += val;
            });
          }
        });

        // 5. Calculate Final Averages
        const formattedData = Object.keys(groupedBySprint).map(sprint => {
          const node = groupedBySprint[sprint];
          const dataPoint = { sprint: sprint, dataPoints: node.count };
          
          Object.keys(node.scores).forEach(metric => {
            dataPoint[metric] = Math.round((node.scores[metric] / node.count) * 10) / 10;
          });
          return dataPoint;
        });

        formattedData.sort((a, b) => {
          const numA = parseInt((a.sprint || '').replace(/\D/g, ''), 10) || 0;
          const numB = parseInt((b.sprint || '').replace(/\D/g, ''), 10) || 0;
          return numA - numB;
        });

        setMetricKeys(Array.from(uniqueMetrics));
        setChartData(formattedData);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load trends:", error);
        setIsLoading(false);
      }
    };

    fetchAndAggregateData();
  }, [tenantId, studentId]);

  // --- DYNAMIC RENDERERS ---
  
  // Translates the raw average into the nearest custom label from the dictionary
  const formatYAxis = (val) => {
    // If the graph tick is an exact match for a custom point, show the custom label
    if (scaleConfig.dictionary[val]) return scaleConfig.dictionary[val];
    return val; 
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <p style={{ fontWeight: 'bold', margin: '0 0 10px 0', color: '#003366' }}>{label}</p>
          {payload.map((entry, index) => {
             const rawScore = entry.value;
             const roundedScore = Math.round(rawScore);
             const customLabel = scaleConfig.dictionary[roundedScore] ? ` (${scaleConfig.dictionary[roundedScore]})` : '';
             
             return (
              <p key={index} style={{ color: entry.color, margin: '5px 0', fontSize: '14px', fontWeight: 'bold' }}>
                {entry.name}: {rawScore} {customLabel}
              </p>
            );
          })}
          <p style={{ marginTop: '10px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Based on {payload[0].payload.dataPoints} teacher submissions
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) return <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Calculating Trends...</div>;

  if (chartData.length === 0) {
    return (
      <div style={{ height: '250px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '2px dashed #ccc' }}>
        <p style={{ color: '#666', fontWeight: 'bold' }}>No General Ed behavior data logged yet.</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 5px 0', color: '#003366', fontSize: '20px' }}>General Ed Behavior Averages</h3>
      <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>Building Scale Applied</p>
      
      <div style={{ height: '300px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="sprint" tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} />
            
            {/* THE SHAPE-SHIFTING Y-AXIS */}
            <YAxis 
              domain={scaleConfig.domain} 
              ticks={scaleConfig.ticks} 
              tickFormatter={formatYAxis}
              tick={{ fill: '#666', fontSize: 12 }} 
              axisLine={false} 
              tickLine={false} 
            />
            
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: '15px' }} />
            
            {metricKeys.map((metric, index) => (
              <Line 
                key={metric}
                type="monotone" 
                dataKey={metric} 
                stroke={colors[index % colors.length]} 
                strokeWidth={3} 
                dot={{ r: 4 }} 
                activeDot={{ r: 6 }} 
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}